import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { isAudioUnlocked, pauseClip, playClip, resumeClip, stopClip, unlockAudio } from "../lib/audio";
import { useListenLock, useRetryLock } from "../lib/play";
import { AnswerDock, ThemeStrip } from "../components/PlayDock";

type Round = {
  finished?: boolean;
  index: number;
  total: number;
  score: number;
  theme: { name: string; color: string; icon: string };
  audioUrl: string;
  clipStartSeconds: number;
  durationMs: number;
  startedAt: string;
  paused?: boolean;
};

type Reveal = {
  accepted?: boolean;
  canonicalAnswer: string;
  justification: string;
  title: string;
  artist: string;
  year: number;
  score: number;
  nextInMs: number;
};

type PauseState = {
  paused: boolean;
  remainingMs?: number;
  phase?: "playing" | "reveal";
  startedAt?: string;
  durationMs?: number;
  nextInMs?: number;
  error?: string;
};

export default function SoloPlay() {
  const nav = useNavigate();
  const [round, setRound] = useState<Round | null>(() => {
    const raw = sessionStorage.getItem("themix_solo_round");
    return raw ? (JSON.parse(raw) as Round) : null;
  });
  const [answer, setAnswer] = useState("");
  const [msg, setMsg] = useState("");
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [remain, setRemain] = useState(30);
  const [paused, setPaused] = useState(false);
  const [needSound, setNeedSound] = useState(!isAudioUnlocked());
  const [retryUntil, setRetryUntil] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { listenLock, listenLeft } = useListenLock(round?.index, round?.startedAt, Boolean(round) && !reveal && !paused);
  const { retryLock, retryLeft } = useRetryLock(retryUntil, Boolean(round) && !reveal && !paused);

  useEffect(() => {
    if (!round || round.finished) {
      nav("/solo");
    }
  }, [round, nav]);

  useEffect(() => {
    if (!round || reveal || paused) return;
    const parsed = Date.parse(round.startedAt);
    const end = Number.isFinite(parsed) ? parsed + round.durationMs : Date.now() + round.durationMs;
    const tick = () => {
      const left = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setRemain(left);
      if (left <= 0) void skip(true);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [round?.index, round?.startedAt, round?.durationMs, reveal, paused]);

  useEffect(() => {
    if (!round || reveal || needSound) return;
    if (paused) {
      pauseClip();
      return;
    }
    playClip(round.audioUrl, round.clipStartSeconds);
    return () => pauseClip();
  }, [round?.index, reveal, needSound]);

  useEffect(() => {
    if (!round || reveal || needSound) return;
    if (paused) pauseClip();
    else resumeClip();
  }, [paused, needSound, reveal, round]);

  useEffect(() => {
    setRetryUntil(0);
  }, [round?.index]);

  function clearRevealTimer() {
    if (revealTimer.current) {
      clearTimeout(revealTimer.current);
      revealTimer.current = null;
    }
  }

  function scheduleNext(ms: number) {
    clearRevealTimer();
    revealTimer.current = setTimeout(() => {
      void next();
    }, Math.max(0, ms));
  }

  async function togglePause() {
    const path = paused ? "/api/solo/resume" : "/api/solo/pause";
    const res = await api<PauseState>(path, { method: "POST" });
    if (res.error && res.error !== "paused") {
      setMsg(res.error);
      return;
    }
    const nowPaused = Boolean(res.paused);
    setPaused(nowPaused);
    if (res.remainingMs != null) setRemain(Math.ceil(res.remainingMs / 1000));
    if (!nowPaused && res.phase === "playing" && round && res.startedAt && res.durationMs != null) {
      const nextRound = { ...round, startedAt: res.startedAt, durationMs: res.durationMs, paused: false };
      sessionStorage.setItem("themix_solo_round", JSON.stringify(nextRound));
      setRound(nextRound);
    }
    if (nowPaused) {
      pauseClip();
      clearRevealTimer();
    } else if (res.phase === "reveal") {
      scheduleNext(res.nextInMs ?? res.remainingMs ?? 5000);
    } else {
      resumeClip();
    }
  }

  async function submit() {
    if (!answer.trim() || reveal || paused || listenLock || retryLock) return;
    const res = await api<
      Reveal & { accepted?: boolean; error?: string; paused?: boolean; retryInMs?: number }
    >("/api/solo/answer", {
      method: "POST",
      body: JSON.stringify({ answer }),
    });
    if (typeof res.retryInMs === "number" && res.retryInMs > 0) {
      setRetryUntil(Date.now() + res.retryInMs);
    }
    if (res.paused) {
      setPaused(true);
      return;
    }
    if (res.error === "cooldown") return;
    if ("error" in res && res.error) {
      setMsg(res.error);
      return;
    }
    if (!res.accepted && !("canonicalAnswer" in res && res.canonicalAnswer)) {
      setMsg("Pas tout à fait…");
      setAnswer("");
      return;
    }
    if (res.canonicalAnswer) showReveal(res);
  }

  async function skip(timeout = false) {
    if (reveal || paused || listenLock) return;
    const res = await api<Reveal & { paused?: boolean; error?: string }>("/api/solo/skip", { method: "POST" });
    if (res.paused || res.error === "paused") {
      setPaused(true);
      return;
    }
    void timeout;
    showReveal(res);
  }

  function showReveal(res: Reveal) {
    stopClip();
    setReveal(res);
    setPaused(false);
    scheduleNext(res.nextInMs ?? 5000);
  }

  async function next() {
    const res = await api<Round & { finished?: boolean; score?: number; total?: number; paused?: boolean; error?: string }>(
      "/api/solo/next",
      { method: "POST" },
    );
    if (res.paused || res.error === "paused") {
      setPaused(true);
      return;
    }
    if (res.finished) {
      sessionStorage.setItem("themix_solo_results", JSON.stringify(res));
      nav("/solo/results");
      return;
    }
    sessionStorage.setItem("themix_solo_round", JSON.stringify(res));
    setRound(res);
    setReveal(null);
    setPaused(false);
    setAnswer("");
    setMsg("");
    setRetryUntil(0);
  }

  if (!round) return null;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <ThemeStrip
        theme={round.theme}
        timer={
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-display text-3xl tabular-nums">{remain}s</span>
            <button
              type="button"
              onClick={() => void togglePause()}
              className="rounded-2xl border border-cream/30 px-3 py-2 text-sm font-semibold"
            >
              {paused ? "Reprendre" : "Pause"}
            </button>
          </div>
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto py-3">
        {paused && (
          <div className="rounded-3xl bg-pink/20 border border-pink p-6 text-center font-display text-3xl">
            Partie en pause
          </div>
        )}
        <div className="text-center">
          <p className="opacity-70">
            {round.index} / {round.total} · {round.score} pt
          </p>
          <div className={`eq my-6 ${paused ? "opacity-40" : ""}`} aria-hidden>
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          {needSound && (
            <button
              type="button"
              className="rounded-2xl bg-gold text-ink px-4 py-3 font-semibold"
              onClick={() => {
                void unlockAudio();
                setNeedSound(false);
              }}
            >
              Activer le son
            </button>
          )}
        </div>
        {reveal && !paused ? (
          <div className="rounded-3xl bg-ink-2 p-5 text-center mt-4">
            <p className="font-display text-3xl">{reveal.canonicalAnswer}</p>
            <p className="mt-2 opacity-80">{reveal.justification}</p>
            <p className="mt-3">
              {reveal.title} — {reveal.artist} ({reveal.year})
            </p>
          </div>
        ) : reveal && paused ? (
          <p className="text-center opacity-80">La suite reprendra à la reprise.</p>
        ) : null}
      </div>
        <AnswerDock
          value={answer}
          onChange={setAnswer}
          onSubmit={() => void submit()}
          locked={Boolean(reveal) || paused || listenLock}
          listenLock={listenLock}
          listenLeft={listenLeft}
          retryLock={retryLock}
          retryLeft={retryLeft}
          keepOpen
          inputRef={inputRef}
          message={msg}
          extra={
            <button
              type="button"
              disabled={Boolean(reveal) || paused || listenLock}
              onMouseDown={(e) => e.preventDefault()}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => void skip()}
              className="rounded-2xl border border-cream/30 py-3 disabled:opacity-50"
            >
              Je ne sais pas
            </button>
          }
        />
    </div>
  );
}
